/**
 * Pattern: 3-Bar Play (Rake Trades / Jake Ricci)
 *
 * Rake's signature continuation entry:
 *   - Bar -2 (igniting bar): wide range, ≥ 1.5× recent ATR
 *   - Bar -1 (rest bar): inside bar (HIGH ≤ bar -2's HIGH AND LOW ≥ bar -2's LOW),
 *                        body ≤ 50% of bar -2's range
 *   - Bar 0 (entry bar): breakout marubozu — close beyond bar -1's extreme
 *                        in the trade direction, body ≥ 70% of bar 0's range
 *
 * Direction implied by which extreme broke:
 *   - LONG  if bar 0 close > bar -1 high
 *   - SHORT if bar 0 close < bar -1 low
 *
 * If the trade's direction doesn't match the implied direction, the
 * pattern doesn't fire (not the same setup the trader took).
 *
 * Pure function. No side effects.
 */

const normalizeDirection = (raw) => {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (s === 'long' || s === 'buy' || s === 'l') return 'long';
  if (s === 'short' || s === 'sell' || s === 's') return 'short';
  return null;
};

const findEntryBarIdx = (bars, entryTs) => {
  if (!Array.isArray(bars) || bars.length === 0) return -1;
  let idx = -1;
  for (let i = 0; i < bars.length; i++) {
    const t = Number(bars[i]?.time);
    if (!Number.isFinite(t)) continue;
    if (t <= entryTs) idx = i;
    else break;
  }
  return idx;
};

const simpleATR = (bars, endIdx, period = 14) => {
  if (endIdx < period) return null;
  let sum = 0;
  let count = 0;
  for (let i = endIdx - period + 1; i <= endIdx; i++) {
    const cur = bars[i];
    const prev = bars[i - 1];
    if (!cur || !prev) continue;
    const hi = Number(cur.high);
    const lo = Number(cur.low);
    const pc = Number(prev.close);
    if (![hi, lo, pc].every(Number.isFinite)) continue;
    sum += Math.max(hi - lo, Math.abs(hi - pc), Math.abs(lo - pc));
    count++;
  }
  return count > 0 ? sum / count : null;
};

export const THREE_BAR_IGNITING_MIN_ATR = 1.5;
export const THREE_BAR_REST_MAX_BODY_RATIO = 0.50;
export const THREE_BAR_BREAKOUT_MIN_BODY_RATIO = 0.70;

export const detectThreeBarPlay = ({ bars, entryTs, direction, options = {} } = {}) => {
  const dir = normalizeDirection(direction);
  if (!dir) return null;
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(entryTs)) return null;

  const ignitingMinAtr = options.ignitingMinAtr || THREE_BAR_IGNITING_MIN_ATR;
  const restMaxBodyRatio = options.restMaxBodyRatio || THREE_BAR_REST_MAX_BODY_RATIO;
  const breakoutMinBodyRatio = options.breakoutMinBodyRatio || THREE_BAR_BREAKOUT_MIN_BODY_RATIO;

  const entryIdx = findEntryBarIdx(bars, entryTs);
  if (entryIdx < 2) return null;  // need 3 bars: -2, -1, entry

  const igniting = bars[entryIdx - 2];
  const rest = bars[entryIdx - 1];
  const breakout = bars[entryIdx];
  if (!igniting || !rest || !breakout) return null;

  // ── Igniting bar checks ────────────────────────────────────────
  const ignHi = Number(igniting.high);
  const ignLo = Number(igniting.low);
  const ignRange = ignHi - ignLo;
  if (!Number.isFinite(ignRange) || ignRange <= 0) return null;

  const atr = simpleATR(bars, entryIdx - 2);
  if (!Number.isFinite(atr) || atr <= 0) return null;
  if (ignRange < atr * ignitingMinAtr) return null;

  // ── Rest bar (inside bar) checks ───────────────────────────────
  const restHi = Number(rest.high);
  const restLo = Number(rest.low);
  const restOp = Number(rest.open);
  const restCl = Number(rest.close);
  if (![restHi, restLo, restOp, restCl].every(Number.isFinite)) return null;

  // Inside-bar: high ≤ prev high AND low ≥ prev low
  if (restHi > ignHi || restLo < ignLo) return null;

  const restBody = Math.abs(restCl - restOp);
  if (restBody > ignRange * restMaxBodyRatio) return null;

  // ── Breakout bar checks ────────────────────────────────────────
  const brkHi = Number(breakout.high);
  const brkLo = Number(breakout.low);
  const brkOp = Number(breakout.open);
  const brkCl = Number(breakout.close);
  if (![brkHi, brkLo, brkOp, brkCl].every(Number.isFinite)) return null;
  const brkRange = brkHi - brkLo;
  if (brkRange <= 0) return null;

  // Implied direction from which extreme broke
  let impliedDirection = null;
  if (brkCl > restHi) impliedDirection = 'long';
  else if (brkCl < restLo) impliedDirection = 'short';
  if (!impliedDirection || impliedDirection !== dir) return null;

  // Body must be at least breakoutMinBodyRatio of range (marubozu-ish)
  const brkBody = Math.abs(brkCl - brkOp);
  if (brkBody < brkRange * breakoutMinBodyRatio) return null;

  return {
    type: '3-bar-play',
    score: 1,
    maxScore: 1,
    aligned: true,
    details: `Igniting ${(ignRange / atr).toFixed(1)}× ATR · inside-bar body ${((restBody / ignRange) * 100).toFixed(0)}% · breakout body ${((brkBody / brkRange) * 100).toFixed(0)}%`,
    evidence: {
      ignitingRange: ignRange, ignitingAtrMultiple: ignRange / atr,
      restBodyRatio: restBody / ignRange,
      breakoutBodyRatio: brkBody / brkRange,
      impliedDirection,
    },
  };
};
